import { zodResolver } from "@hookform/resolvers/zod";
import { signInSchema, type SignInInput } from "@iagenix/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { AuthLayout } from "@/components/auth-layout";
import { FormField } from "@/components/form-field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient, authErrorMessage, authPost } from "@/lib/auth-client";
import { queries } from "@/lib/queries";

const searchSchema = z.object({
  redirecionar: z.string().optional(),
  erro: z.string().optional(),
});

export const Route = createFileRoute("/entrar")({
  validateSearch: searchSchema,
  component: EntrarPage,
});

function safeRedirect(target: string | undefined): string {
  return target && target.startsWith("/") && !target.startsWith("//") ? target : "/app";
}

function EntrarPage() {
  const { redirecionar, erro } = Route.useSearch();
  const navigate = useNavigate();
  const providers = useQuery(queries.authProviders());
  const [modo, setModo] = useState<"senha" | "link">("senha");
  const [linkEnviado, setLinkEnviado] = useState(false);
  // Fase 9: conta com 2FA pede o código do aplicativo depois da senha.
  const [pedeCodigo, setPedeCodigo] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [formError, setFormError] = useState<string | null>(
    erro ? "Não foi possível entrar com o Google." : null,
  );

  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });
  const linkForm = useForm<{ email: string }>({
    resolver: zodResolver(z.object({ email: z.email("Informe um e-mail válido.") })),
    defaultValues: { email: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const { data, error } = await authClient.signIn.email({
      email: values.email,
      password: values.password,
    });
    if (error) {
      setFormError(authErrorMessage(error.code, error.message));
      return;
    }
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      setPedeCodigo(true);
      return;
    }
    await navigate({ to: safeRedirect(redirecionar) });
  });

  const onSubmitCodigo = async (e: SyntheticEvent) => {
    e.preventDefault();
    setFormError(null);
    setVerificando(true);
    try {
      await authPost("/two-factor/verify-totp", { code: codigo.trim() });
      await navigate({ to: safeRedirect(redirecionar) });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Código inválido.");
    } finally {
      setVerificando(false);
    }
  };

  const onSubmitLink = linkForm.handleSubmit(async (values) => {
    setFormError(null);
    const { error } = await authClient.signIn.magicLink({
      email: values.email,
      callbackURL: safeRedirect(redirecionar),
    });
    if (error) {
      setFormError(authErrorMessage(error.code, error.message));
      return;
    }
    setLinkEnviado(true);
  });

  const onGoogle = async () => {
    const { error } = await authClient.signIn.social({
      provider: "google",
      callbackURL: safeRedirect(redirecionar),
      errorCallbackURL: "/entrar?erro=google",
    });
    if (error) toast.error(authErrorMessage(error.code, error.message));
  };

  return (
    <AuthLayout title="Entrar" description="Acesse sua conta do IAGENIX Ads.">
      <div className="space-y-4">
        {formError ? (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}

        {pedeCodigo ? (
          <form onSubmit={(e) => void onSubmitCodigo(e)} className="space-y-4" noValidate>
            <FormField id="codigo" label="Código do aplicativo autenticador" hint="Abra o Google Authenticator, 1Password ou similar e digite os 6 números.">
              <Input
                id="codigo"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
              />
            </FormField>
            <Button type="submit" className="w-full" loading={verificando} disabled={codigo.length !== 6}>
              Confirmar
            </Button>
          </form>
        ) : modo === "senha" ? (
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField id="email" label="E-mail" error={form.formState.errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="voce@agencia.com.br"
                {...form.register("email")}
              />
            </FormField>
            <FormField id="password" label="Senha" error={form.formState.errors.password?.message}>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register("password")}
              />
            </FormField>
            <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
              Entrar
            </Button>
          </form>
        ) : linkEnviado ? (
          <Alert>
            <AlertDescription>
              Se existir uma conta com este e-mail, enviamos um link de acesso. Confira sua caixa de
              entrada (e o spam).
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={onSubmitLink} className="space-y-4" noValidate>
            <FormField
              id="email-link"
              label="E-mail"
              error={linkForm.formState.errors.email?.message}
              hint="Enviamos um link para você entrar sem senha."
            >
              <Input
                id="email-link"
                type="email"
                autoComplete="email"
                placeholder="voce@agencia.com.br"
                {...linkForm.register("email")}
              />
            </FormField>
            <Button type="submit" className="w-full" loading={linkForm.formState.isSubmitting}>
              Enviar link de acesso
            </Button>
          </form>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setModo(modo === "senha" ? "link" : "senha");
              setLinkEnviado(false);
              setFormError(null);
            }}
          >
            {modo === "senha" ? "Entrar com link por e-mail" : "Entrar com senha"}
          </Button>
          {providers.data?.google ? (
            <Button type="button" variant="outline" onClick={onGoogle}>
              Entrar com Google
            </Button>
          ) : null}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Ainda não tem conta?{" "}
          <Link
            to="/cadastro"
            search={{ redirecionar }}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Criar conta
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
